import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import api from '@/services/api'
import type { GridDensity } from '@/pages/pipeline-intelligence/DealGrid'

/**
 * How tightly the pipeline grid is packed — a per-user preference (register B1).
 *
 * "Zero and eight are hard to tell apart at current density. To be resolved with
 * density modes rather than a single size increase." A single larger size would
 * cost every user rows-per-screen to fix one person's legibility, so it is a
 * choice, it is remembered, and compact stays the default.
 *
 * Stored on the user profile, not in localStorage: a preference that follows the
 * person to another machine is the one they expect, and the profile route
 * already exists for exactly this.
 */
const KEY = ['user-profile'] as const

interface ProfileShape {
  gridDensity?: GridDensity
}

async function readProfile(): Promise<ProfileShape> {
  const res = await api.get<{ data: ProfileShape }>('/settings/profile')
  return res.data?.data ?? {}
}

export function useGridDensity() {
  const queryClient = useQueryClient()

  const { data } = useQuery({
    queryKey: KEY,
    queryFn: readProfile,
    // A preference, not live data — one read per session is plenty.
    staleTime: Infinity,
    retry: 1,
  })

  const mutation = useMutation({
    mutationFn: async (density: GridDensity) => {
      await api.patch('/settings/profile', { data: { gridDensity: density } })
      return density
    },
    // Applied immediately: the whole point is to see the change, and the write
    // is a preference — a failure costs the setting, never any deal data.
    onMutate: async (density) => {
      await queryClient.cancelQueries({ queryKey: KEY })
      const previous = queryClient.getQueryData<ProfileShape>(KEY)
      queryClient.setQueryData<ProfileShape>(KEY, { ...(previous ?? {}), gridDensity: density })
      return { previous }
    },
    onError: (_err, _density, context) => {
      if (context?.previous) queryClient.setQueryData(KEY, context.previous)
    },
  })

  return {
    density: data?.gridDensity ?? 'compact',
    setDensity: mutation.mutate,
  }
}
